import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CONVERSATION_ENDED } from '@rsos/constants/chatStatuses';
import {
  RCS_ACCEPT_STATUS_ACCEPTED,
  RCS_CONVERSATION_STATUS_ACTIVE,
  RCS_CONVERSATION_STATUS_PENDING,
} from '@rsos/constants/rcsStatuses';
import useHasAccessToGoogleRCS from '@rsos/hooks/useHasAccessToGoogleRCS';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import {
  getMostRecentConversationAction,
  getHasConversationBeenInitiated,
  getIsConversationEnded,
  getIsConversationPending,
  useGetConversation,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import { RootState } from '../../../app';
import useShouldRenderTF911 from '../../../hooks/useShouldRenderTF911';
import { getChatEntries, getHighestCreatedAtKey } from './helpers';
import {
  type KeyOfChatComponentType,
  type ChatComponents,
  type ChatComponentType,
} from './typings';

interface UseChatComponentsResult {
  chatComponents: ChatComponents;
  primaryChatKey: KeyOfChatComponentType | null;
  shouldRenderAgencyShareChat: boolean;
  bothChatsEnabled: boolean;
  multipleChats: boolean;
  selectedCallerID: string | null;
  isChatDisabled: boolean;
  shouldRenderCollaboratorChat: boolean;
  soloActiveChat: string | null;
}

export const useChatComponents = (callerID = ''): UseChatComponentsResult => {
  const hasAccessToGoogleRCS = useHasAccessToGoogleRCS();
  // This is for the FREE TF911 module
  const isTF911Enabled = useIsTF911Enabled();
  // This is for the PAID TF911 module

  const reduxCallerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );
  const selectedCallerID = callerID || reduxCallerID || '';

  const isCallReceived = useSelector(
    (state: RootState) =>
      state.emergencyCalls.callQueue[selectedCallerID]?.agencyShareChat
        ?.isCallReceived,
  );

  const isCallShared = useSelector(
    (state: RootState) =>
      state.emergencyCalls.callQueue[selectedCallerID]?.agencyShareChat
        ?.isCallShared,
  );

  const shouldRenderAgencyShareChat = Boolean(isCallReceived || isCallShared);

  const collaborator = useSelector((state: StateWithEmergencyCalls) => {
    if (!selectedCallerID) {
      return null;
    }
    return state.emergencyCalls.callQueue[selectedCallerID]?.collaborator;
  });

  const shouldRenderCollaboratorChat = Boolean(collaborator?.conversationID);

  const tf911Conversation = useSelector(
    (state: RootState) => state.textFrom911.conversations[selectedCallerID],
  );

  const conversation = useGetConversation(selectedCallerID);

  // Get the rcs session if it's available
  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  // To future, revisit selectedCallerID when we support texting random number
  // This checks whether to render TF911 or not, based on whether the call is ANI/ALI with specific classes of service
  const shouldRenderTF911 = useShouldRenderTF911(selectedCallerID);

  // For styling
  const bothChatsEnabled = useMemo(() => {
    return shouldRenderAgencyShareChat && shouldRenderTF911;
  }, [shouldRenderAgencyShareChat, shouldRenderTF911]);

  const isFreeTF911Module = useMemo(
    () => isTF911Enabled && shouldRenderTF911,
    [isTF911Enabled, shouldRenderTF911],
  );

  // Determine which chat component to render
  const chatComponents = useMemo(() => {
    const shownChats: ChatComponents = {
      conversations: null,
      rcs: null,
      tf911: null,
    };

    if (conversation?.source) {
      shownChats['conversations'] = {
        isActive:
          (getHasConversationBeenInitiated(conversation) &&
            !getIsConversationEnded(conversation)) ||
          getIsConversationPending(conversation),
        lastUpdated: getMostRecentConversationAction(conversation),
      };
    }

    if (hasAccessToGoogleRCS && rcsConversation && rcsConversation.data) {
      shownChats['rcs'] = {
        isActive:
          (rcsConversation.data.accept_status === RCS_ACCEPT_STATUS_ACCEPTED &&
            rcsConversation.data.conversation_status ===
              RCS_CONVERSATION_STATUS_ACTIVE) ||
          rcsConversation.data.conversation_status ===
            RCS_CONVERSATION_STATUS_PENDING,
        lastUpdated: rcsConversation.data.last_updated_time,
      };
    }

    if (isFreeTF911Module) {
      if (tf911Conversation) {
        shownChats['tf911'] = {
          isActive:
            tf911Conversation.data?.custom?.STATUS !== CONVERSATION_ENDED,
          lastUpdated: tf911Conversation.data?.createdAt || 0,
        };
      }

      if (Object.values(shownChats).every(chat => chat === null)) {
        shownChats['tf911'] = { isActive: false, lastUpdated: 0 };
      }
    }

    return shownChats;
  }, [
    hasAccessToGoogleRCS,
    isFreeTF911Module,
    conversation,
    tf911Conversation,
    rcsConversation,
  ]);

  const soloActiveChat = useMemo(() => {
    const { nonNullEntries, activeEntries } = getChatEntries(chatComponents);

    // Multiple active — show all, no constraint
    if (activeEntries.length > 1) return null;

    // Exactly one active — constrain to that chat
    if (activeEntries.length === 1) return activeEntries[0][0];

    // None active — fall back to highest createdAt among non-null entries
    return getHighestCreatedAtKey(nonNullEntries);
  }, [chatComponents]);

  const primaryChatKey = useMemo((): ChatComponentType | null => {
    const { nonNullEntries, activeEntries } = getChatEntries(chatComponents);

    // Active chats take priority — pick the most recently created active one
    if (activeEntries.length > 0) {
      return getHighestCreatedAtKey(activeEntries) as ChatComponentType;
    }

    // No active chats — fall back to highest createdAt among non-null entries
    return getHighestCreatedAtKey(nonNullEntries) as ChatComponentType;
  }, [chatComponents]);

  const allChatsNull = Object.values(chatComponents).every(v => v === null);
  const chatTypeCount = Object.values(chatComponents).filter(
    v => v !== null,
  ).length;

  const isChatDisabled =
    allChatsNull &&
    !shouldRenderAgencyShareChat &&
    !shouldRenderCollaboratorChat;

  const multipleChats =
    chatTypeCount +
      Number(shouldRenderAgencyShareChat) +
      Number(shouldRenderCollaboratorChat) >
    1;

  return {
    chatComponents,
    shouldRenderAgencyShareChat,
    shouldRenderCollaboratorChat,
    bothChatsEnabled,
    multipleChats,
    selectedCallerID,
    isChatDisabled,
    soloActiveChat,
    primaryChatKey,
  };
};

export * from './typings';
